import React, { useMemo, useRef, useState } from 'react'
import { Alert, Breadcrumb, Col, Container, Form, Overlay, Row, Spinner, Table, Tooltip as BootstrapTooltip } from 'react-bootstrap'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import classNames from 'classnames'

import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useGetAllRolesQuery } from '../../../store/role/role'
import { useAppSelector } from '../../../store/hooks'
import { CreateEmailTypePayload, UpdateEmailTypePayload, useCreateEmailTypeMutation, useUpdateEmailTypeMutation } from '../../../store/integrations/emailTypes'
import { MultiSelectDropdown, MultiSelectOption } from '../../../components/MultiSelectDropdown/MultiSelectDropdown'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import buttonStyles from '../../../components/Button/Button.module.scss'
import { FormFeedback } from '../../../components/Form/FormFeedback'
import { getRoleDisplayName } from '../../../components/Roles/utils'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { Button } from '../../../components/Button/Button'
import { TagElement } from '../../../components/Tag/Tag'
import { EmailAction, EmailType, EmailTypeKind } from '../../../models/EmailType'
import { Role } from '../../../models/Role'

import { UpdateSystemEmailTypeModal } from './UpdateSystemEmailTypeModal'
import { EMAIL_TEMPLATE_IP_HIERARCHY_TYPE_MAPPING, useCopyEmailTemplate, useEmailType } from './hooks'

import './EmailTypes.scss'
import { SelectDropdown, SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { EMAIL_ACTION_LABELS, EMAIL_ACTION_OPTIONS } from './utils'
import { TemplateSelectRow } from '../../../components/InputParameterHierarchy/Template/TemplateSelectRow'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'

const _USER_ROLES_TOOLTIP_TITLE = 'Users with selected roles can send emails that get added to this Email Type and trigger automation.'

const _PURPOSE_INFO_HEADER_MAPPING = {
  [EmailTypeKind.REPLY]: 'Reply to incoming emails',
  [EmailTypeKind.NEW]: 'Start new conversations',
}

const _PURPOSE_INFO_BODY_MAPPING = {
  [EmailTypeKind.REPLY]: 'Categorize and generate responses to incoming emails',
  [EmailTypeKind.NEW]: 'Create outreach emails to engage clients and teammates',
}

const userRoleToSelectOption = (role: Role): MultiSelectOption => {
  return {
    id: role.id,
    label: getRoleDisplayName(role),
  }
}

interface FormValues {
  name: string
  kind: EmailTypeKind
  description: string
  aiAssistantInstructions: string
  roleIds: string[]
  accountEmailAction?: EmailAction
  userEmailAction?: EmailAction
}

const emailTypeToFormValues = (emailType: EmailType | null, copied: boolean): FormValues => {
  if (!emailType) {
    return {
      name: 'New Email Type',
      kind: EmailTypeKind.REPLY,
      description: '',
      aiAssistantInstructions: '',
      roleIds: [],
      accountEmailAction: 'doNotReply',
      userEmailAction: 'doNotReply',
    }
  }
  const {
    name,
    kind,
    detectionConfig: { description, aiAssistantInstructions },
  } = emailType
  const roleIds = emailType.kind === EmailTypeKind.REPLY ? emailType.roleIds : []
  const accountEmailAction = emailType.kind === EmailTypeKind.REPLY ? emailType.accountEmailAction : undefined
  const userEmailAction = emailType.kind === EmailTypeKind.REPLY ? emailType.userEmailAction : undefined
  return { name: copied ? `Copy of ${name}` : name, kind, description, aiAssistantInstructions, roleIds, accountEmailAction, userEmailAction }
}

const formDataToCreateEmailTypePayload = (formData: FormValues, newName?: string): CreateEmailTypePayload => {
  const { name, kind, description, aiAssistantInstructions, roleIds, accountEmailAction, userEmailAction } = formData
  const detectionConfig = { description, aiAssistantInstructions }
  return { name: newName ?? name, kind, detectionConfig, ...(kind === EmailTypeKind.REPLY && { roleIds, accountEmailAction, userEmailAction }) }
}

const formDataToUpdateEmailTypePayload = (
  formData: FormValues,
  dirtyFields: Partial<Record<keyof FormValues, boolean | boolean[] | undefined>>,
): UpdateEmailTypePayload => {
  const {
    name: isNameDirty,
    description: isDescriptionDirty,
    aiAssistantInstructions: isAIAssistantInstructionsDirty,
    roleIds: isRoleIdsDirty,
    accountEmailAction: isAccountEmailActionDirty,
    userEmailAction: isUserEmailActionDirty,
  } = dirtyFields
  const { name, description, aiAssistantInstructions, roleIds, accountEmailAction, userEmailAction } = formData
  return {
    ...(isNameDirty && { name }),
    ...((isDescriptionDirty || isAIAssistantInstructionsDirty) && {
      detectionConfig: {
        description,
        aiAssistantInstructions,
      },
    }),
    ...(isRoleIdsDirty && { roleIds }),
    ...(isAccountEmailActionDirty && { accountEmailAction }),
    ...(isUserEmailActionDirty && { userEmailAction }),
  }
}

export const EmailTypeView: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const { hasScope } = useZocksAuth()
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)

  const { operation = null, emailTypeId = '' } = useParams()

  const copied = operation === 'copy'

  const navigate = useNavigate()
  const { trackingClient, tracked } = useTracking()

  const [showUpdateSystemModal, setShowUpdateSystemModal] = useState<boolean>(false)
  const [emailTypeCreateIsLoading, setEmailTypeCreateIsLoading] = useState<boolean>(false)

  const { emailType, fetchEmailType, isLoading: emailTypeLoading } = useEmailType(emailTypeId)
  const { copyTemplates } = useCopyEmailTemplate({ emailType })

  const getDefaultValues = async () => emailTypeToFormValues(await fetchEmailType(), copied)

  const form = useForm<FormValues>({ defaultValues: getDefaultValues })

  const hasIpHierarchyAdminScope = hasScope('**', 'iph:admin')

  const {
    formState: { isDirty, dirtyFields },
    handleSubmit,
    watch,
    control,
  } = form

  const updateEmailTypeScope = { scope: 'email_type:update', superScope: ['email_type:admin'] }
  const updateUserEmailTypeSettingsScope = { scope: 'email_type:user_settings_update', superScope: ['email_type:admin'] }

  const name = watch('name')
  const kind = watch('kind')
  const accountEmailAction = watch('accountEmailAction')

  const userActionSelectOptions = useMemo(() => {
    return createEmailActionOptions(accountEmailAction)
  }, [accountEmailAction])

  const fromSystemEmailType = !!emailType?.systemEmailTypeId

  const createNewEmailTypeFromSystem =
    fromSystemEmailType && (!isDirty || !!dirtyFields.name || !!dirtyFields.description || !!dirtyFields.aiAssistantInstructions)

  const { data: roles, isFetching: isFetchingRoles } = useGetAllRolesQuery(
    {
      accountId,
      externalOnly: true,
    },
    { refetchOnMountOrArgChange: true },
  )

  const [updateEmailType, { isLoading: emailTypeUpdateIsLoading }] = useUpdateEmailTypeMutation()
  const [createEmailType] = useCreateEmailTypeMutation()

  const roleOptions = useMemo(() => roles?.data.map(userRoleToSelectOption) ?? [], [roles])

  const handleCancel = () => {
    navigate('/workspace/setup/email-types')
  }

  const handleSaveButtonClicked = async () => {
    if (createNewEmailTypeFromSystem && !copied) {
      setShowUpdateSystemModal(true)
    } else {
      const onSubmit = (formData: FormValues) => handleSaveEmailType(formData)
      await handleSubmit(onSubmit)()
    }
  }

  const handleUpdateEmailType = async (formData: FormValues) => {
    try {
      const payload = formDataToUpdateEmailTypePayload(formData, dirtyFields)
      await updateEmailType({ accountId, emailTypeId, payload }).unwrap()
      if (dirtyFields.accountEmailAction) {
        trackingClient.track({
          eventName: TrackEvent.EMAIL_AUTOMATION_SETTINGS_CHANGED,
          eventDetails: { emailTypeId, accountEmailAction: formData.accountEmailAction },
        })
      }
      if (dirtyFields.userEmailAction) {
        trackingClient.track({
          eventName: TrackEvent.EMAIL_AUTOMATION_USER_SETTINGS_CHANGED,
          eventDetails: { emailTypeId, userEmailAction: formData.userEmailAction },
        })
      }

      navigate('/workspace/setup/email-types')
    } catch (e) {
      console.error(e)
    }
  }

  const handleCreateEmailType = async (formData: FormValues, newName?: string) => {
    setEmailTypeCreateIsLoading(true)
    try {
      const payload = formDataToCreateEmailTypePayload(formData, newName)
      const newEmailType = await createEmailType({ accountId, payload }).unwrap()
      if (copied) {
        await copyTemplates(newEmailType.id)
      }
      navigate('/workspace/setup/email-types')
    } catch (e) {
      console.error(e)
    } finally {
      setEmailTypeCreateIsLoading(false)
    }
  }

  const _handleSaveEmailType = async (formData: FormValues, newName?: string) => {
    if (!emailTypeId || copied || createNewEmailTypeFromSystem) {
      await handleCreateEmailType(formData, newName)
      return
    }
    await handleUpdateEmailType(formData)
  }

  const handleSaveEmailType = tracked(
    _handleSaveEmailType,
    {
      eventName: emailTypeId
        ? createNewEmailTypeFromSystem
          ? TrackEvent.EMAIL_TYPE_CREATED_FROM_SYSTEM
          : copied
            ? TrackEvent.EMAIL_TYPE_COPIED
            : TrackEvent.EMAIL_TYPE_UPDATED
        : TrackEvent.EMAIL_TYPE_CREATED,
    },
    {
      eventDetailsExtractor: (formData: FormValues, newEmailTypeName?: string) => {
        return newEmailTypeName ? { name: newEmailTypeName } : {}
      },
    },
  )

  const isLoading = emailTypeLoading || isFetchingRoles || emailTypeCreateIsLoading || emailTypeUpdateIsLoading

  const handleConfirm = async (newName?: string) => {
    const onSubmit = (formData: FormValues) => handleSaveEmailType(formData, newName)
    await handleSubmit(onSubmit)()
  }

  function createEmailActionOptions(defaultAction?: EmailAction): SelectOption[] {
    return [
      ...(defaultAction
        ? [
            {
              id: 'default',
              label: `Use default (${EMAIL_ACTION_LABELS[defaultAction]})`,
            },
          ]
        : []),
      ...EMAIL_ACTION_OPTIONS,
    ]
  }

  return (
    <>
      {showUpdateSystemModal && (
        <UpdateSystemEmailTypeModal
          show={showUpdateSystemModal}
          onCancel={() => setShowUpdateSystemModal(false)}
          onConfirm={handleConfirm}
          emailTypeName={name}
        />
      )}
      <Container className='email-type-view d-flex flex-column zocks-gap-1'>
        {isLoading && (
          <div className='d-flex align-items-center justify-content-center'>
            <Spinner />
          </div>
        )}
        {!isLoading && (
          <>
            <Row>
              <Col className='p-0'>
                <Breadcrumb>
                  <Breadcrumb.Item
                    linkProps={{
                      to: '/workspace/setup/email-types',
                      className: `${buttonStyles.button} ${buttonStyles.linkButton} text-decoration-underline`,
                    }}
                    linkAs={NavLink}
                  >
                    Email Types
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active>{name}</Breadcrumb.Item>
                </Breadcrumb>
              </Col>
            </Row>
            {emailTypeId && fromSystemEmailType ? (
              <Row>
                <Col className='p-0 d-flex gap-2'>
                  <TagElement>System</TagElement>
                </Col>
              </Row>
            ) : null}
            <Row>
              <Col className='p-0'>
                <Form.Label className='label-medium'>Email type name</Form.Label>
                <Controller
                  name={'name'}
                  control={control}
                  render={({ field }) => {
                    return (
                      <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={updateEmailTypeScope}>
                        <Form.Control type='text' value={field.value} onChange={field.onChange} placeholder='Email type name' />
                      </ScopedBlock>
                    )
                  }}
                />
              </Col>
            </Row>
            {emailType ? <PurposeInfo emailType={emailType} /> : null}
            {!operation ? (
              <Row>
                <Col className='d-flex flex-column zocks-gap-0.75 p-0'>
                  <span className='label-medium'>Purpose</span>
                  <Controller
                    name='kind'
                    control={control}
                    render={({ field }) => {
                      return (
                        <>
                          <PurposeRadioButton
                            kind={EmailTypeKind.REPLY}
                            checked={field.value === EmailTypeKind.REPLY}
                            onChange={() => field.onChange(EmailTypeKind.REPLY)}
                          />
                          <PurposeRadioButton
                            kind={EmailTypeKind.NEW}
                            checked={field.value === EmailTypeKind.NEW}
                            onChange={() => field.onChange(EmailTypeKind.NEW)}
                          />
                        </>
                      )
                    }}
                  />
                </Col>
              </Row>
            ) : null}
            <Row className='mt-2'>
              <Col className='p-0'>
                <h5>Configuration</h5>
              </Col>
            </Row>
            <Row>
              <Col className='p-0'>
                <div className='d-flex gap-2'>
                  <Form.Label className='paragraph-medium'>Description</Form.Label>
                </div>
                <div className='flex-grow-1 d-flex zocks-form-container'>
                  <Controller
                    name={'description'}
                    control={control}
                    render={({ field }) => {
                      return (
                        <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={updateEmailTypeScope}>
                          <Form.Control as='textarea' rows={4} value={field.value} onChange={field.onChange} placeholder='Email type description' />
                        </ScopedBlock>
                      )
                    }}
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col className='p-0'>
                <div className='d-flex gap-2'>
                  <Form.Label className='paragraph-medium'>AI Assistant instructions</Form.Label>
                  <AIAssistantInstructionsInfo />
                </div>
                <Controller
                  name={'aiAssistantInstructions'}
                  control={control}
                  render={({ field }) => {
                    return (
                      <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={updateEmailTypeScope}>
                        <Form.Control as='textarea' rows={4} value={field.value} onChange={field.onChange} placeholder='Add instructions' />
                      </ScopedBlock>
                    )
                  }}
                />
              </Col>
            </Row>
            {kind === EmailTypeKind.REPLY ? (
              <Row>
                <Col className='d-flex flex-column p-0'>
                  <div className='d-flex gap-2'>
                    <Form.Label className='paragraph-medium'>User roles</Form.Label>
                    <Tooltip className='paragraph-small position-fixed' title={_USER_ROLES_TOOLTIP_TITLE} placement='top' offset={[0, 10]}>
                      <i className='bi bi-info-square' />
                    </Tooltip>
                  </div>
                  <div className='email-type-user-roles-select-dropdown-container'>
                    <Controller
                      name={'roleIds'}
                      control={control}
                      rules={{ required: 'Select at least 1 user role' }}
                      render={({ field, fieldState }) => {
                        return (
                          <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={updateEmailTypeScope}>
                            <>
                              <MultiSelectDropdown
                                name='emailTypeUserRolesIds'
                                placeholder='Select at least 1 user role'
                                options={roleOptions}
                                initialSelectedOptions={field.value}
                                onSelectedChanged={field.onChange}
                                disabled={emailTypeLoading || isFetchingRoles}
                              />
                              {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                            </>
                          </ScopedBlock>
                        )
                      }}
                    />
                  </div>
                </Col>
              </Row>
            ) : null}
            <Row className='zocks-mt-3.5 zocks-mb-3'>
              <Col className='p-0'>
                <h4 className='mb-1'>Template</h4>
                <div className='paragraph-medium'>Choose which template to use for this email type. Your personal choice overrides the account default.</div>
                <Table className={`mt-4 templatesTable`}>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Your choice</th>
                      <th>Account default</th>
                      {hasIpHierarchyAdminScope ? <th>User-specific</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {emailType ? (
                      <TemplateSelectRow
                        templateSelectionSubHeader={`For ${emailType.name} email type`}
                        type={EMAIL_TEMPLATE_IP_HIERARCHY_TYPE_MAPPING[emailType.kind]}
                        typeId='email-template'
                        context={{
                          emailTypeId: emailType.id,
                          userId: currentUserId,
                          defaultTypeId: emailType.systemEmailTypeId,
                        }}
                      />
                    ) : null}
                  </tbody>
                </Table>
              </Col>
            </Row>
            {kind === EmailTypeKind.REPLY ? (
              <Row className='mt-2'>
                <Col className='p-0 d-flex flex-column gap-3'>
                  <h5>Automation</h5>
                  <Alert variant='warning' className='d-flex gap-3 align-items-center mb-0'>
                    <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                    <span className='zocks-text-gray-900'>
                      Your AI Assistant categorizes incoming emails by Email Type and takes action automatically. Choose what happens for this Email Type.
                    </span>
                  </Alert>
                  <ScopedBlock missingScopeAction={MissingScopeAction.HIDE} scope={updateEmailTypeScope}>
                    <div className='d-flex flex-column gap-2'>
                      <h6>Account default</h6>
                      <Form.Label className='paragraph-medium'>Set default for all users in account</Form.Label>
                      <div className='w-25'>
                        <Controller
                          name={'accountEmailAction'}
                          control={control}
                          render={({ field, fieldState }) => {
                            return (
                              <>
                                <SelectDropdown
                                  options={EMAIL_ACTION_OPTIONS}
                                  initialSelectedOption={EMAIL_ACTION_OPTIONS.find((option) => option.id === field.value)}
                                  onOptionSelected={(action) => field.onChange(action.id)}
                                  disabled={emailTypeLoading || isFetchingRoles}
                                />
                                {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                              </>
                            )
                          }}
                        />
                      </div>
                    </div>
                  </ScopedBlock>
                  <div className='d-flex flex-column gap-2'>
                    <ScopedBlock missingScopeAction={MissingScopeAction.HIDE} scope={updateEmailTypeScope}>
                      <h6>Your setting</h6>
                    </ScopedBlock>

                    <Form.Label className='paragraph-medium'>Override account default for your emails (optional)</Form.Label>
                    <div className='w-25'>
                      <Controller
                        name={'userEmailAction'}
                        control={control}
                        render={({ field, fieldState }) => {
                          return (
                            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={updateUserEmailTypeSettingsScope}>
                              <SelectDropdown
                                options={userActionSelectOptions}
                                initialSelectedOption={userActionSelectOptions.find((option) => option.id === (field.value ?? 'default'))}
                                onOptionSelected={(action) => field.onChange(action.id === 'default' ? null : action.id)}
                                disabled={emailTypeLoading || isFetchingRoles}
                              />
                              {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                            </ScopedBlock>
                          )
                        }}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            ) : null}
            <Row>
              <Col className='d-flex align-items-center p-0 mt-4 justify-content-lg-end gap-3'>
                <Button variant='secondary' onClick={handleCancel}>
                  Cancel
                </Button>
                <Button variant='primary' onClick={handleSaveButtonClicked} disabled={!name || (!fromSystemEmailType && !copied && !isDirty)}>
                  <i className='bi bi-check-lg' />
                  Save
                </Button>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </>
  )
}

const AIAssistantInstructionsInfo = () => {
  const [show, setShow] = useState(false)
  const target = useRef(null)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showInfo = () => {
    setShow(true)
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }

  const hideInfo = () => {
    if (hideTimeoutRef.current) {
      return
    }
    hideTimeoutRef.current = setTimeout(() => {
      setShow(false)
      hideTimeoutRef.current = null
    }, 1000)
  }

  return (
    <>
      <i ref={target} onMouseEnter={showInfo} onMouseLeave={hideInfo} className='bi bi-info-square' />

      <Overlay target={target.current} show={show} placement='top'>
        {(props) => (
          <BootstrapTooltip id='tooltip-with-link' onMouseEnter={showInfo} onMouseLeave={hideInfo} {...props}>
            <div className='p-1 text-start'>
              <span className='paragraph-small'>
                Give guidelines to the AI assistant on how to approach its answer generation and important information it needs to include.{' '}
                <a
                  href='https://help.zocks.io'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='button-link-small zocks-text-white'
                  onClick={(e) => e.stopPropagation()} // Prevent parent event
                >
                  Help article
                </a>
              </span>
            </div>
          </BootstrapTooltip>
        )}
      </Overlay>
    </>
  )
}

interface EmailTypeKindIconProps {
  emailType: EmailType
}

export const EmailTypeKindIcon: React.FC<EmailTypeKindIconProps> = ({ emailType }) => {
  const { kind } = emailType

  return (
    <div
      className={classNames('d-flex align-items-center justify-content-center flex-shrink-0 rounded email-type-icon', {
        'reply-email-type-icon': kind === EmailTypeKind.REPLY,
        'new-email-type-icon': kind === EmailTypeKind.NEW,
      })}
    >
      <i
        className={classNames('flex-shrink-0 fs-5 bi', {
          'bi-envelope-arrow-down': kind === EmailTypeKind.REPLY,
          'bi-envelope-plus': kind === EmailTypeKind.NEW,
        })}
      />
    </div>
  )
}

interface PurposeInfoProps {
  emailType: EmailType
}

const PurposeInfo: React.FC<PurposeInfoProps> = ({ emailType }) => {
  const { kind } = emailType

  const headerText = _PURPOSE_INFO_HEADER_MAPPING[kind]
  const bodyText = _PURPOSE_INFO_BODY_MAPPING[kind]

  return (
    <Row>
      <Col className='p-0'>
        <div className='d-flex align-items-center zocks-gap-0.75'>
          <EmailTypeKindIcon emailType={emailType} />
          <div className='d-flex flex-column'>
            <span className='paragraph-medium'>{headerText}</span>
            <span className='paragraph-small'>{bodyText}</span>
          </div>
        </div>
      </Col>
    </Row>
  )
}

interface PurposeRadioButtonProps {
  checked: boolean
  onChange: () => void
  kind: EmailTypeKind
}

const PurposeRadioButton: React.FC<PurposeRadioButtonProps> = ({ checked, onChange, kind }) => {
  const headerText = _PURPOSE_INFO_HEADER_MAPPING[kind]
  const bodyText = _PURPOSE_INFO_BODY_MAPPING[kind]

  return (
    <div className='d-flex zocks-gap-0.5'>
      <Form.Check
        className='email-type-kind-input-wrapper d-flex align-items-center justify-content-center m-0'
        type='radio'
        name='kind-group'
        id={`kind-group-${kind}`}
        checked={checked}
        onChange={onChange}
      />
      <label className='d-flex flex-column' htmlFor={`kind-group-${kind}`}>
        <span className='paragraph-medium'>{headerText}</span>
        <span className='paragraph-small'>{bodyText}</span>
      </label>
    </div>
  )
}
